import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  BondListing,
  BondListingApiItem,
  DeleteBondListingResult,
  UpsertBondListingRequest,
} from './bond-listing.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondListingService {
  private readonly endpoint = apiUrl('bondlisting');

  constructor(private readonly http: HttpClient) {}

  getBondListings(): Observable<BondListing[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeBondListing(item))),
    );
  }

  createBondListing(payload: UpsertBondListingRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateBondListing(id: number, payload: UpsertBondListingRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteBondListing(id: number): Observable<DeleteBondListingResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteBondListingResult => ({ kind: 'success' })),
      catchError(() => of<DeleteBondListingResult>({
        kind: 'error',
        message: 'Unable to delete bond listing at this time. Please try again.',
      })),
    );
  }

  private normalizeBondListing(item: BondListingApiItem): BondListing {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: BondListingApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): BondListingApiItem[] {
    if (Array.isArray(response)) {
      return response as BondListingApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as BondListingApiItem[];
      }

      if (Array.isArray(items)) {
        return items as BondListingApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
